import React, { useState, useEffect } from "react";
import { useAdmin } from "../../context/AdminContext";
import { MessageSquare, Eye, CheckCircle, Clock, Archive, AlertCircle } from "lucide-react";

const ContactSubmissionsManager = ({ onStatsUpdate }) => {
  const { adminAPI } = useAdmin();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState("tous");

  const statusOptions = [
    { value: "nouveau", label: "Nouveau", color: "bg-red-100 text-red-800", icon: AlertCircle },
    { value: "lu", label: "Lu", color: "bg-blue-100 text-blue-800", icon: Eye },
    { value: "traite", label: "Traité", color: "bg-green-100 text-green-800", icon: CheckCircle },
    { value: "archive", label: "Archivé", color: "bg-gray-100 text-gray-800", icon: Archive }
  ];

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const response = await adminAPI.getContactSubmissions();
      setSubmissions(response.data);
      
      // Count new submissions for stats
      const newCount = response.data.filter(sub => sub.status === "nouveau").length;
      if (onStatsUpdate) onStatsUpdate(newCount);
    } catch (error) {
      console.error("Erreur lors du chargement des demandes:", error);
      setMessage({ type: "error", text: "Erreur lors du chargement" });
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await adminAPI.updateSubmissionStatus(id, newStatus);
      setMessage({ type: "success", text: "Statut mis à jour avec succès !" });
      loadSubmissions();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      setMessage({ type: "error", text: "Erreur lors de la mise à jour" });
    }
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const filteredSubmissions = filter === "tous" 
    ? submissions 
    : submissions.filter(sub => sub.status === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Demandes de Contact</h2>
          <p className="text-gray-600 mt-1">
            {submissions.length} demande(s) total(es) • 
            {submissions.filter(s => s.status === "nouveau").length} nouvelle(s)
          </p>
        </div>

        {/* Filter */}
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter("tous")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              filter === "tous" 
                ? "bg-blue-900 text-white" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Tous
          </button>
          {statusOptions.map(status => {
            const count = submissions.filter(sub => sub.status === status.value).length;
            return (
              <button
                key={status.value}
                onClick={() => setFilter(status.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  filter === status.value 
                    ? "bg-blue-900 text-white" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {status.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {message && (
        <div className={`mb-6 flex items-center space-x-2 p-4 rounded-lg ${
          message.type === "success" 
            ? "bg-green-50 text-green-800 border border-green-200" 
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Submissions List */}
      <div className="space-y-4">
        {filteredSubmissions.map((submission) => {
          const statusInfo = getStatusInfo(submission.status);
          const StatusIcon = statusInfo.icon;
          
          return (
            <div key={submission.id} className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <MessageSquare className="w-5 h-5 text-blue-900" />
                    <h3 className="text-lg font-semibold text-gray-900">{submission.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      <StatusIcon className="w-3 h-3 inline mr-1" />
                      {statusInfo.label}
                    </span>
                    {submission.status === "nouveau" && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                        Nouveau
                      </span>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Email :</span>
                      <p className="text-gray-600">{submission.email}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Téléphone :</span>
                      <p className="text-gray-600">{submission.phone}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Type de projet :</span>
                      <p className="text-gray-600">{submission.project_type}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Date :</span>
                      <p className="text-gray-600">{formatDate(submission.created_at)}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="font-medium text-gray-700">Message :</span>
                    <p className="text-gray-600 mt-1 bg-gray-50 p-3 rounded-lg">
                      {submission.message}
                    </p>
                  </div>

                  {/* Status Update Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map(status => {
                      if (status.value === submission.status) return null;
                      
                      return (
                        <button
                          key={status.value}
                          onClick={() => updateStatus(submission.id, status.value)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 ${
                            status.value === "nouveau" 
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : status.value === "lu"
                              ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              : status.value === "traite"
                              ? "bg-green-50 text-green-700 hover:bg-green-100"
                              : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          Marquer comme {status.label.toLowerCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filteredSubmissions.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === "tous" ? "Aucune demande" : `Aucune demande "${getStatusInfo(filter).label.toLowerCase()}"`}
            </h3>
            <p className="text-gray-600">
              {filter === "tous" 
                ? "Les demandes de contact apparaîtront ici." 
                : "Changez le filtre pour voir d'autres demandes."
              }
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {submissions.length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statusOptions.map(status => {
              const count = submissions.filter(sub => sub.status === status.value).length;
              const percentage = submissions.length > 0 ? Math.round((count / submissions.length) * 100) : 0;
              
              return (
                <div key={status.value} className="text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${status.color} mb-2`}>
                    <status.icon className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-sm text-gray-600">{status.label}</div>
                  <div className="text-xs text-gray-500">({percentage}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionsManager;